<script lang="ts">
  import CustomSpace from "$components/space/CustomSpace.svelte";
  import DialogueModal from "$components/+shared/Modal/DialogueModal.svelte";
  import Dropdown from "$components/+design-system/Dropdown/Dropdown.svelte";
  import Action from "$components/+design-system/Dropdown/fields/Action.svelte";
  import DocPanel from "$components/knowledge-base/DocPanel.svelte";
  import Reader from "$components/editor/Reader.svelte";
  import { saveToClipboard } from "app/utils/clipboard";
  import { page } from "$app/stores";
  import { hasPermission } from "$stores/permissions";
  import { sidePanel } from "$stores/sidePanel";
  import { alerts } from "$stores/alert";
  import { DocumentPermission } from "@outverse-video/outverse-api-client-lib";
  import IconButtonTrigger from "$components/+design-system/Dropdown/fields/IconButtonTrigger.svelte";
  import {
    community,
    communitySlug,
    whiteLabelFlag,
    documentFeedbackFlag,
  } from "app/stores/community";
  import { Dots, Link, Trash, Right, Download, Copy } from "$components/+shared/OVIcon";
  import { getStores } from "$stores";
  import DocPillStatus from "app/components/knowledge-base/DocPillStatus.svelte";
  import { afterNavigate, beforeNavigate } from "$app/navigation";
  import NextPreviousDocFooter from "$components/knowledge-base/NextPreviousDocFooter.svelte";
  import OVIcon from "$components/+shared/OVIcon/OVIcon.svelte";
  import { YDoc, type YdocStore } from "$components/editor/ydoc";
  import Avatar from "app/components/+shared/Avatar/Avatar.svelte";
  import { isDarkTheme } from "app/stores/userPreferences";
  import { categoryURL, sectionURL } from "app/utils/urls";
  import { slugify } from "app/utils/strings";
  import { versionHistoryModalOpen } from "app/components/editor/extensions/Collaboration/versions.js";
  import Separator from "$components/+shared/Separator/Separator.svelte";
  import cx from "classnames";
  import DocFeedback from "$components/knowledge-base/DocFeedback/DocFeedback.svelte";
  import { submitFeedback } from "../../../utils.js";
  import { reportError } from "app/utils/errors.js";

  export let data;

  const { docs, user } = getStores();

  let openDeleteDialogue = false;
  let readOnly = true;
  let isSaving = false;
  let ydoc: YdocStore | null;
  let reader: Reader;

  $: doc = docs.hydrateDoc(data.doc);
  $: canEdit = hasPermission($doc.myPermissions, DocumentPermission.ChangeDocument);

  beforeNavigate((props) => {
    if (isSaving) {
      props.cancel();
      alerts.warning("Please wait – document update in progress");
    } else {
      readOnly = true;
    }
  });

  afterNavigate(() => {
    if (!$user.isAuthed) return;
    if (canEdit) {
      ydoc = YDoc({
        documentName: `document-${data.doc.uuid}`,
        user: $user,
        useWebRTC: true,
        useLocalDB: true,
        initialDocument: data.doc.content.crdt,
        json: data.doc.content.json,
      });
    } else {
      ydoc = null;
    }
  });
  $: sidePanel.setPageDefault(DocPanel, $doc.title, { doc, ydoc, versionHistoryModalOpen });

  const handleFeedback = async (event: CustomEvent<{ isPositive: boolean }>) => {
    try {
      const isPositive = event.detail.isPositive;
      await submitFeedback($user.isAuthed, isPositive, $doc.latestVersionId);
      alerts.success("Feedback submitted");
    } catch (error) {
      reportError(error as Error);
      alerts.error("An error occurred.");
    }
  };

  // This code block is responsible for setting up the breadcrumb URL based on the current document's folder.
  // It retrieves the folder and parent folder from the `docs` store and generates the appropriate URL based on whether the folder has a parent or not.
  let breadcrumbURL: string;
  $: folder = docs.getFolder($doc.folder.uuid);
  $: parentFolder = $folder.parentUuid ? docs.getFolder($folder.parentUuid) : null;
  // $: {
  //   if ($parentFolder) {
  //     breadcrumbURL = categoryURL({
  //       communitySlug: $community.slug,
  //       categorySlug: slugify($folder.title),
  //       categoryUuid: $folder.uuid,
  //     });
  //   }
  //   if(!$parentFolder && $folder) {
  //     breadcrumbURL = categoryURL({
  //       communitySlug: $community.slug,
  //       categorySlug: slugify($folder.title),
  //       categoryUuid: $folder.uuid,
  //     });
  //   }
  //   if()
  //   }
  $: breadcrumbURL =
    $parentFolder || $folder.categoryType === "category"
      ? categoryURL({
          communitySlug: $community.slug,
          categorySlug: slugify($folder.title),
          categoryUuid: $folder.uuid,
        })
      : sectionURL({
          communitySlug: $community.slug,
          sectionSlug: slugify($folder.title),
          sectionUuid: $folder.uuid,
        });
</script>

{#if $doc && $doc.content}
  <!-- re-init editor when doc changes to prevent ydoc clashing  -->
  {#key $doc.uuid}
    <CustomSpace
      title={$doc.title}
      {user}
      isPublished={$doc.isPublished}
      color={$community.primaryColor}
      {versionHistoryModalOpen}
      {canEdit}
      bind:readOnly
      bind:reader
      {doc}
      {ydoc}
      content={$doc.content.json || null}
      onSaveDoc={async (json, text, crdt, title, isDraft) => {
        try {
          if (!$doc) return json;
          // prevent draft auto-saves from interferring with publishes
          if ((isSaving || $versionHistoryModalOpen) && isDraft) {
            console.log("save in progress, ignoring draft autosave");
            return;
          }
          if (isDraft) {
            $doc.draftUpdatedAt = new Date();
            doc.saveDraft({ content: { json, crdt, text, title } }, json);
          } else {
            console.log("saving document");
            isSaving = true;
            await doc.waitForSave(() => {
              $doc.content = { ...$doc.content, json, crdt, text, title };
              $doc.title = title;
            });
            console.log("saved document");
          }
          return json;
        } finally {
          isSaving = false;
        }
      }}
    >
      <span slot="title" class="inline-flex items-center gap-2 overflow-hidden">
        {#if !$doc.folder.isDefault}
          <a
            class="flex items-center gap-2 overflow-hidden text-gray-500/70 transition-colors hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
            href={breadcrumbURL}
          >
            {#if $doc.folder.icon}
              {@const icon =
                $isDarkTheme && $doc.folder.iconDark ? $doc.folder.iconDark : $doc.folder.icon}
              <div>
                <Avatar mainImage={icon} radius="rounded-[3px]" />
              </div>
            {/if}
            <span class="truncate">
              {$doc.folder.title}
            </span>
          </a>
          <OVIcon icon={Right} size="size-3 text-gray-500/70 dark:text-gray-400" />
        {/if}
        {#if $doc.icon}
          {@const icon = $isDarkTheme && $doc.iconDark ? $doc.iconDark : $doc.icon}
          <div>
            <Avatar mainImage={icon} radius="rounded-[3px]" />
          </div>
        {/if}
        <span class="truncate">
          {$doc.title}
        </span>
      </span>

      <!-- Next/Prev doc and branding footer -->
      <div slot="footer" let:hasContentsBlock class:w-fit={hasContentsBlock} class="mx-auto pt-8">
        <!-- Fit to width of text (adapts to contents block) -->
        <div
          class={cx("@4xl/main-panel-container:gap-4", {
            "relative grid grid-cols-[minmax(30ch,_800px)_minmax(max-content,_1fr)]":
              hasContentsBlock,
          })}
        >
          <div class="mx-auto w-full" class:max-w-[800px]={!hasContentsBlock}>
            {#if readOnly}
              {#if $documentFeedbackFlag}
                <!-- Doc feedback -->
                <div class="mb-4 flex w-full items-center justify-center px-4">
                  <DocFeedback on:feedbackClicked={handleFeedback} />
                </div>
              {/if}
              <!-- TODO: move next prev footer here so is only visible when not editing -->
            {/if}
            <NextPreviousDocFooter currentDocument={doc} />

            {#if !$whiteLabelFlag}
              <!-- Powered by Outverse footer -->
              <div class="mt-8 flex flex-col space-y-8">
                <Separator class="w-full" />
                <a
                  href="https://www.outverse.com/?utm_campaign=poweredBy&utm_medium=docs&utm_source={$communitySlug}"
                  target="_blank"
                  referrerpolicy="no-referrer"
                  class="ml-auto text-xs text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300"
                  >Powered by Outverse</a
                >
              </div>
            {/if}
          </div>
          {#if hasContentsBlock}
            <div
              class="@4xl/main-panel-container:block @4xl/main-panel-container:min-w-[250px] @4xl/main-panel-container:pl-10"
            ></div>
          {/if}
        </div>
      </div>

      <Dropdown slot="dropdown">
        <IconButtonTrigger
          icon={Dots}
          a11yLabel="Document options"
          slot="trigger"
          let:active
          {active}
        />

        <Action
          icon={Link}
          title="Copy link"
          on:click={() => saveToClipboard($page.url.toString(), alerts.add)}
        />
        <!-- EXPORT TO MARKDOWN -->
        <Action
          icon={Download}
          title="Download as .md file"
          on:click={() => reader.downloadMarkdown($doc.title)}
        />
        <Action icon={Copy} title="Copy markdown" on:click={() => reader.copyMarkdown()} />
        {#if hasPermission($doc.myPermissions, DocumentPermission.DeleteDocument)}
          <Action title="Delete" icon={Trash} danger on:click={() => (openDeleteDialogue = true)} />
        {/if}
      </Dropdown>

      <svelte:fragment slot="pill">
        {#if canEdit}
          <DocPillStatus
            isPublished={$doc.isPublished}
            isEditing={!readOnly}
            {isSaving}
            on:edit={() => (readOnly = false)}
            on:close-edit={() => (readOnly = true)}
          />
        {/if}
      </svelte:fragment>
    </CustomSpace>
  {/key}

  <DialogueModal
    bind:open={openDeleteDialogue}
    title="Delete {$doc.title}?"
    message="Are you sure you want to delete this document?"
    on:confirm={() => {
      doc.delete();
      alerts.success("Deleted document");
    }}
    confirmLabel="Delete"
    danger
  />
{/if}
